import { FC, useEffect, useMemo, useRef, useState } from 'react';

export interface FlightRosterFilterOption {
  value: string;
  label: string;
}

interface FlightRosterMultiValueFilterChipProps {
  label: string;
  value: string[];
  options: FlightRosterFilterOption[];
  isEditing: boolean;
  searchable?: boolean;
  onEditingChange: (editing: boolean) => void;
  onUpdate: (value: string[]) => void;
}

const FlightRosterMultiValueFilterChip: FC<
  FlightRosterMultiValueFilterChipProps
> = ({
  label,
  value,
  options,
  isEditing,
  searchable = false,
  onEditingChange,
  onUpdate,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      onEditingChange(false);
    };

    if (isEditing) document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing, onEditingChange]);

  const filteredOptions = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return options;

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(normalized) ||
        option.value.toLowerCase().includes(normalized),
    );
  }, [options, query]);

  const toggle = (optionValue: string) => {
    setLocalValue((current) =>
      current.includes(optionValue)
        ? current.filter((value) => value !== optionValue)
        : [...current, optionValue],
    );
  };

  const summary =
    value.length === 0
      ? 'All'
      : value
          .map(
            (selected) =>
              options.find((option) => option.value === selected)?.label ??
              selected,
          )
          .join(', ');

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => onEditingChange(!isEditing)}
        className="inline-flex items-center gap-x-1 rounded-xl border border-gray-200 bg-lightGreen px-3 py-1 text-sm hover:bg-lightGreen"
      >
        <span className="font-medium">{label}:</span>
        <span className="max-w-32 truncate">{summary}</span>
      </button>

      {isEditing && (
        <div className="absolute left-0 top-full z-10 mt-2 w-80 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
          <div className="flex flex-col gap-y-4">
            {searchable && (
              <input
                aria-label={`Search ${label}`}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="block w-full rounded-md border-gray-300 text-sm shadow-sm"
                placeholder={`Search ${label.toLowerCase()}`}
              />
            )}

            <div className="max-h-64 overflow-y-auto">
              {filteredOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={localValue.includes(option.value)}
                    onChange={() => toggle(option.value)}
                    className="size-4 rounded border-gray-300"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
              {filteredOptions.length === 0 && (
                <div className="px-2 py-3 text-center text-sm text-gray-500">
                  No matches
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setLocalValue([])}
                className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => {
                  onUpdate(localValue);
                  onEditingChange(false);
                }}
                className="rounded-md bg-secondaryBlue px-4 py-2 text-sm font-medium text-white hover:bg-secondaryBlueShade"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightRosterMultiValueFilterChip;
